import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ChangeDetectionStrategy
} from "@angular/core";
import { NgTemplateOutlet, NgClass, NgStyle } from "@angular/common";
import { FlapButtonComponent } from "../flap-button/flap-button.component";

@Component({
    selector: "rask-vertical-collapsable",
    templateUrl: "./vertical-collapsable.component.html",
    styleUrls: ["./vertical-collapsable.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgTemplateOutlet,
        NgClass,
        FlapButtonComponent,
        NgStyle
    ]
})
export class VerticalCollapsableComponent implements OnChanges {

    @Input()
    set leftClosed(value: boolean) {
        this.update("left", value);
    }
    get leftClosed() {
        return this.closed === "left";
    }

    @Input()
    set rightClosed(value: boolean) {
        this.update("right", value);
    }
    get rightClosed() {
        return this.closed === "right";
    }

    @Input() direction: "left" | "right" | "both" = "left";
    @Input() leftClass = "col-6";
    @Input() flapPosTop = "5px";
    @Input() showBar = false;
    @Input() showFlap = true;

    @Input() rightSideOpenText = "Visa";
    @Input() rightSideCloseText = "Dölj";
    @Input() leftSideOpenText = "Visa";
    @Input() leftSideCloseText = "Dölj";

    @Input() hideLeftButton = false;
    @Input() hideRightButton = false;
    @Output() leftClosedChange = new EventEmitter<boolean>();
    @Output() rightClosedChange = new EventEmitter<boolean>();

    closed: Side = null;

    leftClassSum = "";
    rightClassSum = "";

    ngOnChanges(changes: SimpleChanges): void {
        this.updateClass();
    }

    toggle(side?: Side) {
        if (!side) {
            if (this.closed === "right") {
                this.toggle("left");
                return;
            }
            this.toggle("right");
            return;
        }
        if (this.closed == null) {
            this.closed = side;
        } else {
            this.closed = null;
        }
        this.updateEvents();
        this.updateClass();
    }

    update(side: Side, close: boolean) {
        if (this.closed === side && close) {
            return;
        }
        if (close) {
            this.close(side);
        } else if (this.closed === side) {
            this.open();
        }
        this.updateEvents();
        this.updateClass();
    }

    updateEvents() {
        this.rightClosedChange.emit(this.closed === "right");
        this.leftClosedChange.emit(this.closed === "left");
    }

    flapVisible(side: Side): boolean {
        if (this.useBarForOpening()) {
            return false;
        }
        if (this.flapSideIsClosed(side)) {
            return false;
        }
        if (this.showOpenFlapForClosedSide(side)) {
            return true;
        }
        return this.showCloseFlapIfEnabled(side);
    }

    getText(side: Side) {
        if (this.closed) {
            return side === "left"
                ? this.rightSideOpenText
                : this.leftSideOpenText;
        }
        return side === "left"
            ? this.leftSideCloseText
            : this.rightSideCloseText;
    }

    updateClass() {
        this.leftClassSum = this.getClassList("left", this.leftClass).join(" ");
        this.rightClassSum = this.getClassList("right").join(" ");
    }

    getClassList(side: Side, defaultClass?: string) {
        const classList = [];
        if (defaultClass) {
            classList.push(defaultClass);
        }
        if (this.closed === side) {
            classList.push("closed");
        } else if (this.closed) {
            classList.push("fullsize");
        }

        return classList;
    }

    private readonly flapSideIsClosed = (side) => this.closed === side;

    private readonly useBarForOpening = () => !!this.closed && this.showBar;

    private readonly showOpenFlapForClosedSide = (side: Side) => !!this.closed && side !== this.closed;

    private readonly isFlapHidden = (side: Side) => side === "left" && this.hideLeftButton || side === "right" && this.hideRightButton;

    private readonly showCloseFlapIfEnabled = (side: Side) => !this.isFlapHidden(side) && (this.direction === side || this.direction === "both");

    private close(side: Side) {
        this.closed = side;
    }

    private open() {
        this.closed = null;
    }
}

export type Side = "left" | "right";
